import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, Theme } from '@mui/material';

// Balanced Theme - Medium contrast palette between light and dark
// A soothing, eye-friendly color scheme
const professionalTheme = {
  primary: {
    main: '#546BDA',    // Soft indigo blue
    light: '#7F93E9',
    dark: '#384AAF',
    contrastText: '#ffffff'
  },
  secondary: {
    main: '#61CDBB',    // Mint teal
    light: '#8FDED1',
    dark: '#3DA697',
    contrastText: '#1F2937'
  },
  background: {
    default: '#F1F2F7', // Soft light blue-gray 
    paper: '#FFFFFF'    // White cards/panels
  },
  text: {
    primary: '#2F3A4D',  // Soft dark blue-gray
    secondary: '#5F6B7D'  // Medium gray with blue undertone
  },
  error: {
    main: '#E5534B',     // Softer red
    light: '#F1857F',
    dark: '#C22E26', 
    contrastText: '#FFFFFF'
  },
  warning: {
    main: '#F5B15A',     // Soft amber
    light: '#FACB8D',
    dark: '#D68B1C',
    contrastText: '#2F3A4D'
  },
  info: {
    main: '#55AAEC',     // Soft sky blue
    light: '#87C5F3',
    dark: '#2D88D2',
    contrastText: '#FFFFFF'
  },
  success: {
    main: '#5DB075',     // Soft green
    light: '#8AC99B',
    dark: '#3C8850',
    contrastText: '#FFFFFF'
  },
  grey: {
    50: '#F8FAFC',
    100: '#F1F2F7',
    200: '#E5E7ED',
    300: '#D0D3DC',
    400: '#A3AABF',
    500: '#7B849A',
    600: '#5F6B7D',
    700: '#4A5568',
    800: '#2F3A4D',
    900: '#1F2937',
  },
  divider: 'rgba(47, 58, 77, 0.08)'
};

// Create the context
interface ThemeContextType {
  theme: Theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Create a provider component
interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Create the theme
  const theme = React.useMemo(
    () =>
      createTheme({
        palette: professionalTheme,
        typography: {
          fontFamily: '"Rubik", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          h1: {
            fontWeight: 600,
            fontSize: '2.5rem',
            lineHeight: 1.3,
            letterSpacing: '-0.01em',
            color: '#2F3A4D',
          },
          h2: {
            fontWeight: 600,
            fontSize: '2rem',
            lineHeight: 1.35,
            letterSpacing: '-0.01em',
            color: '#2F3A4D',
          },
          h3: {
            fontWeight: 600,
            fontSize: '1.75rem',
            lineHeight: 1.4,
            color: '#2F3A4D',
          },
          h4: {
            fontWeight: 600,
            fontSize: '1.5rem',
            lineHeight: 1.4,
            color: '#2F3A4D',
          },
          h5: {
            fontWeight: 600,
            fontSize: '1.25rem',
            lineHeight: 1.4,
            color: '#2F3A4D',
          },
          h6: {
            fontWeight: 600,
            fontSize: '1rem',
            lineHeight: 1.4,
            color: '#2F3A4D',
          },
          subtitle1: {
            fontWeight: 600,
            fontSize: '1rem',
            lineHeight: 1.5,
            color: '#2F3A4D',
          },
          subtitle2: {
            fontWeight: 500,
            fontSize: '0.875rem',
            lineHeight: 1.5,
            color: '#2F3A4D',
          },
          body1: {
            fontWeight: 400,
            fontSize: '1rem',
            lineHeight: 1.6,
            color: '#5F6B7D',
          },
          body2: {
            fontWeight: 400,
            fontSize: '0.875rem',
            lineHeight: 1.6,
            color: '#5F6B7D',
          },
          button: {
            fontWeight: 500,
            fontSize: '0.875rem',
            letterSpacing: '0.015em',
            textTransform: 'none',
          },
          caption: {
            fontSize: '0.75rem',
            fontWeight: 400,
            color: '#7B849A',
          },
          overline: {
            fontSize: '0.75rem',
            fontWeight: 500,
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
            color: '#7B849A',
          },
        },
        shape: {
          borderRadius: 8, // Slightly more rounded for a softer feel
        },
        components: {
          MuiButton: {
            defaultProps: {
              disableElevation: false,
            },
            styleOverrides: {
              root: {
                textTransform: 'none',
                borderRadius: '8px',
                fontWeight: 500,
                padding: '8px 18px',
                minHeight: '38px',
                transition: 'all 0.2s ease',
                letterSpacing: '0.015em',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(47, 58, 77, 0.12)'
                },
                '&:active': {
                  transform: 'translateY(0)'
                }
              },
              contained: {
                boxShadow: '0 2px 4px rgba(47, 58, 77, 0.1)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(47, 58, 77, 0.15)',
                },
              },
              containedPrimary: {
                background: 'linear-gradient(135deg, #546BDA 0%, #6B7FE3 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4A5FC8 0%, #546BDA 100%)',
                },
              },
              containedSecondary: {
                background: 'linear-gradient(135deg, #61CDBB 0%, #73D9C8 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #47B9A7 0%, #61CDBB 100%)',
                },
              },
              outlined: {
                borderWidth: '1.5px',
                padding: '7px 17px',
                '&:hover': {
                  borderWidth: '1.5px',
                }
              },
              outlinedPrimary: {
                borderColor: '#546BDA',
                '&:hover': {
                  backgroundColor: 'rgba(84, 107, 218, 0.05)',
                  borderColor: '#384AAF',
                },
              },
              text: {
                '&:hover': {
                  backgroundColor: 'rgba(84, 107, 218, 0.05)',
                }
              },
              sizeLarge: {
                minHeight: '44px',
                padding: '10px 22px',
                fontSize: '1rem',
              },
              sizeSmall: {
                minHeight: '32px',
                padding: '5px 14px',
                fontSize: '0.8125rem',
                borderRadius: '6px',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: '12px',
                boxShadow: '0 2px 12px rgba(47, 58, 77, 0.08)',
                overflow: 'hidden',
                border: '1px solid rgba(47, 58, 77, 0.05)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(47, 58, 77, 0.12)',
                  transform: 'translateY(-2px)',
                },
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                borderRadius: '10px',
                boxShadow: '0 2px 8px rgba(47, 58, 77, 0.08)',
              },
              outlined: {
                border: '1px solid rgba(47, 58, 77, 0.1)',
              },
              elevation1: {
                boxShadow: '0 2px 4px rgba(47, 58, 77, 0.05)',
              },
              elevation2: {
                boxShadow: '0 2px 8px rgba(47, 58, 77, 0.08)',
              },
              elevation3: {
                boxShadow: '0 4px 12px rgba(47, 58, 77, 0.1)',
              },
              elevation4: {
                boxShadow: '0 5px 14px rgba(47, 58, 77, 0.12)',
              },
              elevation6: {
                boxShadow: '0 6px 16px rgba(47, 58, 77, 0.12)',
              },
              elevation8: {
                boxShadow: '0 8px 18px rgba(47, 58, 77, 0.14)',
              },
              elevation12: {
                boxShadow: '0 10px 22px rgba(47, 58, 77, 0.15)',
              },
              elevation24: {
                boxShadow: '0 14px 28px rgba(47, 58, 77, 0.18)',
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: '#546BDA',
                color: '#FFFFFF',
                boxShadow: '0 2px 8px rgba(47, 58, 77, 0.15)',
              },
              colorPrimary: {
                background: 'linear-gradient(135deg, #546BDA 0%, #6B7FE3 100%)',
                color: '#FFFFFF',
              },
              colorDefault: {
                backgroundColor: '#FFFFFF',
                color: '#2F3A4D',
                boxShadow: '0 2px 8px rgba(47, 58, 77, 0.1)',
              }
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                backgroundColor: '#F8FAFC',
                borderRight: '1px solid rgba(47, 58, 77, 0.1)',
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                borderBottom: '1px solid rgba(47, 58, 77, 0.08)',
                padding: '14px 18px',
                color: '#5F6B7D',
                fontSize: '0.875rem',
              },
              head: {
                backgroundColor: '#E5E7ED',
                color: '#2F3A4D',
                fontWeight: 600,
                fontSize: '0.8125rem',
                letterSpacing: '0.02em',
                lineHeight: '1.4',
              },
            },
          },
          MuiTableRow: {
            styleOverrides: {
              root: {
                '&:nth-of-type(odd)': {
                  backgroundColor: 'rgba(241, 242, 247, 0.5)',
                },
                '&:hover': {
                  backgroundColor: 'rgba(84, 107, 218, 0.04) !important',
                },
              },
            },
          },
          MuiTableHead: {
            styleOverrides: {
              root: {
                backgroundColor: '#E5E7ED',
              },
            },
          },
          MuiInputBase: {
            styleOverrides: {
              root: {
                backgroundColor: '#F8FAFC',
                borderRadius: '8px',
                fontSize: '0.875rem',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#F1F2F7',
                },
                '&.Mui-focused': {
                  backgroundColor: '#FFFFFF',
                }
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                borderRadius: '8px',
                backgroundColor: '#F8FAFC',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D0D3DC',
                  borderWidth: '1.5px',
                  transition: 'all 0.2s ease',
                },
                '&:hover': {
                  backgroundColor: '#F1F2F7',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#A3AABF',
                  },
                },
                '&.Mui-focused': {
                  backgroundColor: '#FFFFFF',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#546BDA',
                    borderWidth: '2px',
                  },
                },
                '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E5534B',
                }
              },
              input: {
                padding: '12px 16px',
                height: '1.4375em',
              },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: {
                color: '#5F6B7D',
                fontWeight: 500,
                fontSize: '0.875rem',
                marginBottom: '4px',
                '&.Mui-focused': {
                  color: '#546BDA',
                },
                '&.Mui-error': {
                  color: '#E5534B',
                }
              }
            }
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                marginTop: '10px',
                marginBottom: '16px',
              },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                minHeight: '48px',
                padding: '12px 20px',
                borderRadius: '0',
                marginRight: '1px',
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  fontWeight: 600,
                  color: '#546BDA',
                  backgroundColor: 'rgba(84, 107, 218, 0.08)',
                },
                '&:hover': {
                  backgroundColor: 'rgba(84, 107, 218, 0.04)',
                  color: '#546BDA',
                }
              }
            }
          },
          MuiTabs: {
            styleOverrides: {
              indicator: {
                height: '3px',
                backgroundColor: '#546BDA',
                borderRadius: '3px 3px 0 0',
              },
              root: {
                minHeight: '48px',
                borderBottom: '1px solid #E5E7ED',
              }
            }
          },
          MuiChip: {
            styleOverrides: {
              root: {
                borderRadius: '16px',
                fontWeight: 500,
                fontSize: '0.75rem',
                height: '28px',
                transition: 'all 0.2s ease',
                '& .MuiChip-label': {
                  paddingLeft: '10px',
                  paddingRight: '10px',
                }
              },
              filled: {
                backgroundColor: 'rgba(84, 107, 218, 0.15)',
                color: '#384AAF',
                '&:hover': {
                  backgroundColor: 'rgba(84, 107, 218, 0.2)',
                },
                '&.MuiChip-colorSecondary': {
                  backgroundColor: 'rgba(97, 205, 187, 0.15)',
                  color: '#3DA697',
                  '&:hover': {
                    backgroundColor: 'rgba(97, 205, 187, 0.2)',
                  },
                },
                '&.MuiChip-colorSuccess': {
                  backgroundColor: 'rgba(93, 176, 117, 0.15)',
                  color: '#3C8850',
                  '&:hover': {
                    backgroundColor: 'rgba(93, 176, 117, 0.2)',
                  },
                },
                '&.MuiChip-colorError': {
                  backgroundColor: 'rgba(229, 83, 75, 0.15)',
                  color: '#C22E26',
                  '&:hover': {
                    backgroundColor: 'rgba(229, 83, 75, 0.2)',
                  },
                },
                '&.MuiChip-colorWarning': {
                  backgroundColor: 'rgba(245, 177, 90, 0.15)',
                  color: '#D68B1C',
                  '&:hover': {
                    backgroundColor: 'rgba(245, 177, 90, 0.2)',
                  },
                },
              },
              outlined: {
                borderColor: '#D0D3DC',
                color: '#5F6B7D',
                '&:hover': {
                  backgroundColor: 'rgba(131, 146, 189, 0.08)',
                },
              },
              deleteIcon: {
                color: '#7B849A',
                '&:hover': {
                  color: '#2F3A4D',
                  backgroundColor: 'rgba(47, 58, 77, 0.08)',
                }
              }
            },
          },
          MuiAvatar: {
            styleOverrides: {
              root: {
                backgroundColor: '#546BDA',
                color: '#FFFFFF',
                fontWeight: 500,
                fontSize: '0.875rem',
              },
              colorDefault: {
                backgroundColor: '#546BDA',
              },
              colorPrimary: {
                backgroundColor: '#546BDA',
              },
              colorSecondary: {
                backgroundColor: '#61CDBB',
                color: '#2F3A4D',
              }
            },
          },
          MuiListItem: {
            styleOverrides: {
              root: {
                borderRadius: '8px',
                '&.Mui-selected': {
                  backgroundColor: 'rgba(84, 107, 218, 0.12)',
                  '&:hover': {
                    backgroundColor: 'rgba(84, 107, 218, 0.16)',
                  },
                },
              },
            },
          },
          MuiListItemIcon: {
            styleOverrides: {
              root: {
                color: '#5F6B7D',
                minWidth: '40px',
              },
            },
          },
          MuiListItemButton: {
            styleOverrides: {
              root: {
                borderRadius: '8px',
                padding: '10px 16px',
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  backgroundColor: 'rgba(84, 107, 218, 0.12)',
                  '&:hover': {
                    backgroundColor: 'rgba(84, 107, 218, 0.16)',
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(84, 107, 218, 0.08)',
                  transform: 'translateY(-1px)',
                }
              },
            },
          },
          MuiDivider: {
            styleOverrides: {
              root: {
                borderColor: '#E5E7ED',
                margin: '8px 0',
              },
              middle: {
                margin: '16px 0',
              }
            },
          },
          MuiAlert: {
            styleOverrides: {
              root: {
                borderRadius: '8px',
                padding: '12px 20px',
                boxShadow: '0 2px 8px rgba(47, 58, 77, 0.08)',
                border: '1px solid',
              },
              standardSuccess: {
                backgroundColor: 'rgba(93, 176, 117, 0.12)',
                color: '#3C8850',
                borderColor: 'rgba(93, 176, 117, 0.25)',
              },
              standardError: {
                backgroundColor: 'rgba(229, 83, 75, 0.12)',
                color: '#C22E26',
                borderColor: 'rgba(229, 83, 75, 0.25)',
              },
              standardWarning: {
                backgroundColor: 'rgba(245, 177, 90, 0.12)',
                color: '#D68B1C',
                borderColor: 'rgba(245, 177, 90, 0.25)',
              },
              standardInfo: {
                backgroundColor: 'rgba(84, 107, 218, 0.12)',
                color: '#384AAF',
                borderColor: 'rgba(84, 107, 218, 0.25)',
              },
              icon: {
                padding: '4px',
              }
            }
          },
          MuiSwitch: {
            styleOverrides: {
              root: {
                width: 46,
                height: 26,
                padding: 0,
              },
              switchBase: {
                padding: 1,
                '&.Mui-checked': {
                  transform: 'translateX(20px)',
                  color: '#FFFFFF',
                  '& + .MuiSwitch-track': {
                    opacity: 1,
                    backgroundColor: '#546BDA',
                  },
                },
              },
              thumb: {
                width: 24,
                height: 24,
                boxShadow: '0 2px 4px rgba(47, 58, 77, 0.2)',
              },
              track: {
                borderRadius: 13,
                backgroundColor: '#D0D3DC',
                opacity: 1,
              },
            },
          },
        },
      }),
    []
  );

  // Apply theme to body element
  useEffect(() => {
    document.body.style.backgroundColor = theme.palette.background.default;
    document.body.style.color = theme.palette.text.primary;
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme }}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useThemeContext = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

// For backward compatibility, provide a dummy toggleTheme function
// This ensures any existing code using toggleTheme won't break
export const useToggleTheme = () => {
  return { toggleTheme: () => console.log('Theme switching disabled') };
};